import { TourStep } from './tour.service';

/** Display title override when tour title differs from sidebar label. */
const TOUR_TITLES: Record<string, string> = {
  Profile: 'My Profile',
};

/** Short descriptions of what the highlighted sidebar section is for. */
const TOUR_DESCRIPTIONS: Record<string, string> = {
  Dashboard:
    'Your command center — see key metrics, recent activity, insights, and quick actions at a glance.',
  'My Activities':
    'Track every action you take on the platform — access a log of your platform activities, including assets, compute tasks, and user actions.',
  'Asset Details':
    'Monitor the status of your asset and access requests and manage them throughout their lifecycle.',
  'Delegated Access':
    'See the owners who granted you access and the scopes you can act on their behalf.',
  'Asset Management':
    'Monitor the status of your asset and access requests and manage them throughout their lifecycle.',
  'My Projects':
    'Access and manage your compute projects from a dedicated workspace once your compute role is approved.',
  Profile:
    'View your account details, change your password, download client credentials, and manage your privacy and terms consents.',
  Support:
    'Find answers in the FAQ, consult the user manual, or contact the MahaAgX support team for assistance.',
  'Manage Assets':
    'Add datasets and models, review your contributed assets, and manage access requests and policies from one place.',
  Organisation:
    'View your organisation details and stay connected with your entity on the MahaAgX platform.',
  'Manage Users':
    'Review user requests, manage organisation members, and monitor organisation user activities.',
  Asset:
    'Oversee platform assets, file uploads, and access requests across the MahaAgX catalogue.',
  'Manage Platform':
    'Administer platform users, organisations, compute requests, challenges, and discussions.',
  'User Activities':
    'View the complete history of all user activities performed across the platform.',
  Delegation:
    'Manage people who can act on your behalf, and review the activities they performed.',
};

/** Sidebar items hidden for a consumer/provider/org-admin delegate session. */
const DELEGATE_HIDDEN_SIDEBAR_LABELS = new Set([
  'My Projects',
  'Manage Delegate',
  'Delegation',
  'Organisation',
]);

export function isDelegatePanelLabel(panelLabel: string): boolean {
  return /\bDelegate$/i.test(String(panelLabel ?? '').trim());
}

/** Expand short sidebar chrome names for the Panel Demo welcome title. */
export function tourPanelLabel(headerLabel: string): string {
  const label = (headerLabel || '').trim() || 'Consumer';
  if (label === 'Org Admin') {
    return 'Organisation Admin';
  }
  if (label === 'Org Admin Delegate') {
    return 'Organisation Admin Delegate';
  }
  return label;
}

export function tourMenuLabelsForPanel(
  menuLabels: string[],
  isDelegatePanel: boolean,
): string[] {
  if (!isDelegatePanel) {
    return menuLabels;
  }
  return menuLabels.filter((label) => !DELEGATE_HIDDEN_SIDEBAR_LABELS.has(label));
}

export function tourTitleForLabel(label: string): string {
  return TOUR_TITLES[label] ?? label;
}

export function tourDescriptionForLabel(label: string): string {
  return (
    TOUR_DESCRIPTIONS[label] ??
    `Explore ${label} to manage this section of the platform.`
  );
}

/**
 * Build tour steps from top-level menu labels + footer labels.
 * Dropdown children and CTA cards are excluded by the caller.
 */
export function buildTourSteps(
  menuLabels: string[],
  footerLabels: string[],
): TourStep[] {
  const seen = new Set<string>();
  const steps: TourStep[] = [];

  for (const label of [...menuLabels, ...footerLabels]) {
    if (!label || seen.has(label)) continue;
    seen.add(label);
    steps.push({
      label,
      title: tourTitleForLabel(label),
      description: tourDescriptionForLabel(label),
    });
  }

  return steps;
}
